/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { actionRefMapper } from "./actionRefMapper";
import { builderArrayMapper } from "./builderArrayMapper";
import { builderBooleanMapper } from "./builderBooleanMapper";
import { builderDataMapper } from "./builderDataMapper";
import { builderDateMapper } from "./builderDateMapper";
import { builderEnumMapper } from "./builderEnumMapper";
import { builderFloatMapper } from "./builderFloatMapper";
import { builderIntegerMapper } from "./builderIntegerMapper";
import { builderMapMapper } from "./builderMapMapper";
import { builderObjectMapper } from "./builderObjectMapper";
import { builderPasswordMapper } from "./builderPasswordMapper";
import { builderStringMapper } from "./builderStringMapper";
import { builderAnyMapper } from "./builderAnyMapper";
import { commentsMapper } from "./commentsMapper";
import { constAnyMapper } from "./constAnyMapper";
import { constArrayMapper } from "./constArrayMapper";
import { expressionMapper } from "./expressionMapper";
import { iconNameMapper } from "./iconNameMapper";
import { integrationRefMapper } from "./integrationRefMapper";
import { oneOfMapper } from "./oneOfMapper";
import { optGroupMapper } from "./optGroupMapper";
import { recursiveMapper } from "./recursiveMapper";
import { responsiveValueMapper } from "./responsiveValueMapper";
import { routeRefMapper } from "./routeRefMapper";
import { scopedTemplateMapper } from "./scopedTemplateMapper";
import { stringTranslateMapper } from "./stringTranslateMapper";
import { stringWithConstMapper } from "./stringWithConstMapper";
import { valueMapper } from "./valueMapper";
import { viewRefMapper } from "./viewRefMapper";
import { constStringMapper } from "./constStringMapper";
import { constEnumMapper } from "./constEnumMapper";
import { constSecretMapper } from "./constSecretMapper";
import { constFloatMapper } from "./constFloatMapper";
import { constBooleanMapper } from "./constBooleanMapper";
import { constObjectMapper } from "./constObjectMapper";
import { constMapMapper } from "./constMapMapper";
import { constPasswordMapper } from "./constPasswordMapper";
import { constIntegerMapper } from "./constIntegerMapper";
import { TGenericModelNode } from "@adapptio/lib-blueprint";
import { TInspectorNode } from "../InspectorNodeTypes";
import { dynamicMapper } from "./dynamicMapper";
import { themeRefMapper } from "./themeRefMapper";

type Mapper = (model: TGenericModelNode) => TInspectorNode;

export const mappersBySchemaName: Record<string, Mapper> = {
	builderAny: builderAnyMapper,
	actionRef: actionRefMapper,
	builderArray: builderArrayMapper,
	builderBoolean: builderBooleanMapper,
	builderData: builderDataMapper,
	builderDate: builderDateMapper,
	builderEnum: builderEnumMapper,
	builderFloat: builderFloatMapper,
	builderInteger: builderIntegerMapper,
	builderMap: builderMapMapper,
	builderObject: builderObjectMapper,
	builderPassword: builderPasswordMapper,
	builderString: builderStringMapper,
	comments: commentsMapper,
	constAny: constAnyMapper,
	constArray: constArrayMapper,
	constBoolean: constBooleanMapper,
	constEnum: constEnumMapper,
	constFloat: constFloatMapper,
	constInteger: constIntegerMapper,
	constMap: constMapMapper,
	constObject: constObjectMapper,
	constPassword: constPasswordMapper,
	constSecret: constSecretMapper,
	constString: constStringMapper,
	dynamic: dynamicMapper,
	expression: expressionMapper,
	iconName: iconNameMapper,
	integrationRef: integrationRefMapper,
	oneOf: oneOfMapper,
	optGroup: optGroupMapper,
	recursive: recursiveMapper,
	responsiveValue: responsiveValueMapper,
	routeRef: routeRefMapper,
	scopedTemplate: scopedTemplateMapper,
	stringTranslate: stringTranslateMapper,
	stringWithConst: stringWithConstMapper,
	themeRef: themeRefMapper,
	value: valueMapper,
	viewRef: viewRefMapper
};

export function getModelMapper(model: TGenericModelNode): Mapper {
	const mapper = mappersBySchemaName[model.schema.name];
	return mapper;
}
